<template>
  <div class="diagnostics-header">
    {{ tr('Diagnostics')
    }}
    <div class="files-info" @click="openFiles()" v-if="!!patientId">
      <span class="material-symbols-outlined icon-button-to-primary"
        :title="tr('Files and Upload')">folder_shared</span>
      <span class="file-count" v-if="reportCount">{{ reportCount }}</span>
    </div>
  </div>
  <div class="tab-container">
    <div class="tab diag-tab" :class="{ 'diag-tab-active': tabGenActive }" id="diag-gen-tab"
      @click="tabDiagClick('Gen')">
      {{ tr('Genomics') }}
      <span class="tab-count" v-if="genomicsCount">{{ genomicsCount }}</span>
    </div>
    <div class="tab diag-tab" :class="{ 'diag-tab-active': tabPathActive }" id="diag-path-tab"
      @click="tabDiagClick('Path')">
      {{ tr('Pathology') }} <span class="tab-count" v-if="pathologyCount">{{ pathologyCount }}
      </span>
    </div>
    <div class="tab diag-tab" :class="{ 'diag-tab-active': tabRadActive }" id="diag-rad-tab"
      @click="tabDiagClick('Rad')">
      {{ tr('Radiology') }}
      <span class="tab-count" v-if="radiologyCount">{{ radiologyCount }}
      </span>
    </div>
  </div>
  <div class="spinner-div" :class="{ 'spinner': showDiagnosticsCardsLoadingSpinner }"></div>

  <div class="diag-list-container">
    <div class="diag-container" :class="{ 'diag-container-active': tabAllActive }" id="diag-all-list">All</div>
    <div class="diag-container" :class="{ 'diag-container-active': tabGenActive }" id="diag-gen-list">
      <div class="container-items">
        <DiagnosticCardsEmptyState v-show="!genomicsCount"></DiagnosticCardsEmptyState>
        <SpecimensCard ref="genomicsCardEl" v-show="genomicsCount" @onSpecimensCountUpdated="setGenomicsCount"
          @onCardsLoading="toggleDiagnosticsSpinner" @onCardsFinishedLoading="toggleDiagnosticsSpinner"></SpecimensCard>
      </div>
    </div>
    <div class="diag-container" :class="{ 'diag-container-active': tabPathActive }" id="diag-path-list">
      <div class="container-items">
        <DiagnosticCardsEmptyState v-if="!pathologyCount"></DiagnosticCardsEmptyState>
        <PathologyCard ref="pathologyCardEl" @onManifestCountChange="setPathologyCount"></PathologyCard>
      </div>
    </div>
    <div class="diag-container" :class="{ 'diag-container-active': tabRadActive }" id="diag-rad-list">
      <div class="container-items">
        <DiagnosticCardsEmptyState v-if="!radiologyCount"></DiagnosticCardsEmptyState>
        <RadiologyCard ref="radiologyCardEl" @onRadiologyCountChange="setRadiologyCount"></RadiologyCard>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineExpose, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { clearCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'
import SpecimensCard from '@/ux3/components/Genomics/Specimens/SpecimensCard.vue'
import PathologyCard from '@/ux3/components/Pathology/Specimens/PathologyCard.vue'
import RadiologyCard from '../../components/Radiology/RadiologyCard.vue'
import DiagnosticCardsEmptyState from '@/ux3/components/EmptyStateViews/DiagnosticCardsEmptyState.vue'
import getReportList from '../ReportViewer/getReportList'

const store = useStore()

const tr = (x) => x // inject('tr')
const tabAllActive = ref(false)
const tabGenActive = ref(true)
const tabPathActive = ref(false)
const tabRadActive = ref(false)
const genomicsCount = ref(0)
const radiologyCount = ref(0)
const pathologyCount = ref(0)
const pathologyCardEl = ref(null)
const radiologyCardEl = ref(null)
const genomicsCardEl = ref(null)
const reportCount = computed(() => store.getters.reportCount || 0)
const patientId = computed(() => store.getters.session?.patient?.id || '')

const showDiagnosticsCardsLoadingSpinner = ref(false)

const clearDiagTabs = () => {
  tabAllActive.value = false
  tabGenActive.value = false
  tabPathActive.value = false
  tabRadActive.value = false
}

const tabLookup = { 'All': tabAllActive, 'Gen': tabGenActive, 'Path': tabPathActive, 'Rad': tabRadActive }
const tabDiagClick = (tab) => {
  clearDiagTabs()
  tabLookup[tab].value = true
}

const refreshATabByName = (tabNames) => {
  tabNames.forEach(tabName => {
    if (tabName === 'PATHOLOGY' && pathologyCardEl.value) {
      pathologyCardEl.value.loadSamples(patientId.value)
    }
    if (tabName === 'RADIOLOGY' && radiologyCardEl.value) {
      radiologyCardEl.value.loadSamples(patientId.value)
    }
    if (tabName === 'GENOMICS' && genomicsCardEl.value) {
      genomicsCardEl.value.loadSamples(patientId.value)
    }
  })
}

const openFiles = () => {
  clearCenterColumn('reports')
}

const setGenomicsCount = (n) => {
  genomicsCount.value = n
}

const setPathologyCount = (n) => {
  pathologyCount.value = n
}

const setRadiologyCount = (n) => {
  radiologyCount.value = n
}

const toggleDiagnosticsSpinner = () => showDiagnosticsCardsLoadingSpinner.value =
  !showDiagnosticsCardsLoadingSpinner.value

watch(patientId, async () => {
  const result = await getReportList(patientId)
  reportCount.value = result?.length || 0
  store.dispatch('reportCount', reportCount.value)
})

defineExpose({ refreshATab: refreshATabByName })
</script>

<style lang="scss" scoped>
.diag-list-container {
  height: calc(91vh - 165px);
}

.container-items {
  margin-top: 0.25rem;
  height: calc(85vh - 165px);
  width: 100%;
}

.diagnostics-header {
  color: var(--Grey800);
  padding: 1rem 1.4rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--Grey200);
  background-color: var(--White);
}

.diag-container {
  display: none;
  padding: 15px;
}

.diag-container-active {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--Grey100);
}

.diag-tab {
  background-color: var(--White);
  font-size: clamp(10px, 3.25cqi, 16px);
}

.diag-tab-active {
  background-color: var(--Grey100);
  color: var(--Black);
}

.diag-container {
  overflow: auto;
}

.files-info {
  display: flex;
}

.file-count {
  display: flex;
  height: 15px;
  min-width: 15px;
  padding: 5px;
  color: #fff;
  background-color: var(--Secondary);
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  font-weight: 800;
  border-radius: 20px;
  margin-left: -11px;
  margin-top: 3px;
}

.spinner-div {
  border-image: conic-gradient(from var(--angle), var(--MarketingMagenta), var(--BlendedOrange), var(--MarketingOrange), var(--BlendedOrange), var(--MarketingMagenta)) 1;
  transition: all linear 300ms;
}

.tab-container {
  overflow: hidden;
}
</style>
